import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { TextField, InputAdornment } from '@mui/material';

import { INDETERMINATE_TEXT, isIndeterminate, usePrevious } from '@paragon/client-tools';

import { forceArray, notNullish } from '@paragon/utilities';

const NO_VALUE = Symbol('NO VALUE (used by FormattedTextInput to distinguish the initial value of usePrevious hooks)');

export function FormattedTextField({
  blurOnEnter,
  canBeIndeterminate,
  canBeNull,
  canSubmitWhenInvalid,
  canSubmitWhenNull,
  disabled,
  endAdornment,
  error,
  formatValue,
  helperText,
  indeterminatePlaceholder,
  nullPlaceholder,
  onDisplayValueChange,
  onSubmit,
  parseValue,
  ref,
  slotProps,
  startAdornment,
  submitContinuously,
  validator,
  validators,
  value,
  ...textFieldProps
}) {
  if (isIndeterminate(value) && canBeIndeterminate !== true) {
    throw new Error('Cannot render FormattedTextField with an indeterminate value');
  }

  const isBlurringDueToEnterKey = useRef(false);

  // do not use _setDisplayValue directly -- see `setDisplayValueRef` below
  const [displayValue, _setDisplayValue] = useState(() =>
    value != null && !isIndeterminate(value) ? formatValue(value) : '',
  );

  // this function is in a ref because it depends on a user-provided callback which might not be memoized,
  //   but we need to use it from effects
  const setDisplayValueRef = useRef();
  setDisplayValueRef.current = function setDisplayValue(newValue) {
    _setDisplayValue(newValue);

    if (onDisplayValueChange && newValue !== displayValue) {
      onDisplayValueChange(newValue);
    }
  };

  useImperativeHandle(ref, () => ({ setDisplayValue: (text) => setDisplayValueRef.current(text) }), []);

  // we don't validate explicitly allowed special values because they are, well, explicitly allowed
  const shouldSkipValidation = useCallback(
    (value) => (value === null && canBeNull) || (isIndeterminate(value) && canBeIndeterminate),
    [canBeNull, canBeIndeterminate],
  );

  const runValidators = useCallback(
    (valueToCheck) => {
      if (shouldSkipValidation(valueToCheck)) return undefined;

      for (const validatorFn of [validator, ...(validators ?? [])].filter(Boolean)) {
        const invalidReason = validatorFn(valueToCheck);
        if (invalidReason) {
          return invalidReason;
        }
      }

      return undefined;
    },
    [validator, validators, shouldSkipValidation],
  );

  const displayValueFailsToParse = shouldSkipValidation(value) ? false : parseValue(displayValue).valid === false;

  const [failedValidation, setFailedValidation] = useState(() => runValidators(value) ?? false);

  const [isEscapingNullValue, setIsEscapingNullValue] = useState(false);
  const [isEscapingIndeterminateValue, setIsEscapingIndeterminateValue] = useState(false);

  // respond to changes in the controlled value
  const previousValue = usePrevious(value, NO_VALUE);
  useEffect(() => {
    if (previousFormatValue === NO_VALUE) return; // do nothing for the first run on mount
    if (value === previousValue) return;

    setIsEscapingIndeterminateValue(false);
    setIsEscapingNullValue(false);
    setFailedValidation(runValidators(value) ?? false);

    setDisplayValueRef.current(value != null && !isIndeterminate(value) ? formatValue(value) : '');
  }, [value, previousValue, formatValue]);

  // ensure display value is updated when the formatter "changes" (produces a different result)
  const previousFormatValue = usePrevious(formatValue, NO_VALUE);
  useEffect(() => {
    if (previousFormatValue === NO_VALUE) return; // do nothing for the first run on mount
    if (value == null || isIndeterminate(value)) return;

    const oldResult = previousFormatValue(value);
    const newResult = formatValue(value);

    if (oldResult !== newResult) {
      setDisplayValueRef.current(newResult ?? '');
    }
  }, [value, formatValue, previousFormatValue]);

  // re-run validation when validator functions change
  useEffect(() => {
    setFailedValidation(runValidators(value) ?? false);
  }, [runValidators, value]);

  function requestSubmitValue(editedValue, flags) {
    // see `FormattedTextField.d.ts` for documentation of flags

    if (isEscapingIndeterminateValue) {
      setIsEscapingIndeterminateValue(false);
    }

    if (isEscapingNullValue) {
      setIsEscapingNullValue(false);
    }

    if (onSubmit) onSubmit(editedValue, flags);
  }

  function handleFocus(event) {
    if (isIndeterminate(value)) {
      setIsEscapingIndeterminateValue(true);
    } else if (value == null && canBeNull) {
      setIsEscapingNullValue(true);
    }

    event.target.select();
  }

  function handleEventAffectingValue(event, origin) {
    const final = origin === 'blur' || origin === 'enter';
    const finalizing = origin === 'enter';

    if (final && event.target.value.trim() === '') {
      if (isEscapingIndeterminateValue) {
        setIsEscapingIndeterminateValue(false);
      } else if (isEscapingNullValue) {
        setIsEscapingNullValue(false);
      }

      if (canSubmitWhenNull === true) {
        requestSubmitValue(null, { event, valid: true, final, finalizing });
      }

      return;
    }

    if (origin === 'change') {
      setDisplayValueRef.current(event.target.value);

      if (submitContinuously !== true) {
        return;
      }
    }

    const parsedResult = parseValue(event.target.value);
    if (!parsedResult.valid) {
      if (canSubmitWhenInvalid === true) {
        requestSubmitValue(null, { event, valid: false, final, finalizing });
      }

      return;
    }

    const validatorFailureReason = runValidators(parsedResult.value);
    if (validatorFailureReason) {
      setFailedValidation(validatorFailureReason);
      return;
    } else if (failedValidation) {
      setFailedValidation(false);
    }

    setDisplayValueRef.current(formatValue(parsedResult.value));

    const valueChanged = value !== parsedResult.value;

    if (valueChanged || submitContinuously || finalizing) {
      requestSubmitValue(parsedResult.value, { event, valid: true, final, finalizing });
    }
  }

  const startAdornmentElement = startAdornment ? (
    <InputAdornment position="start">{startAdornment}</InputAdornment>
  ) : undefined;

  const endAdornmentElement = endAdornment
    ? forceArray(endAdornment)
        .filter(notNullish)
        .map((el) => (
          <InputAdornment position="end" key={typeof el === 'string' ? el : el.key}>
            {el}
          </InputAdornment>
        ))
    : undefined;

  let valueToRender = displayValue;

  if (isIndeterminate(value) && !isEscapingIndeterminateValue) {
    valueToRender = indeterminatePlaceholder ?? INDETERMINATE_TEXT;
  } else if (value == null && !isEscapingNullValue) {
    valueToRender = nullPlaceholder ?? '';
  }

  return (
    <TextField
      fullWidth
      margin="normal"
      variant="outlined"
      disabled={disabled}
      slotProps={{
        ...slotProps,
        input: {
          ...slotProps?.input,
          startAdornment: startAdornmentElement,
          endAdornment: endAdornmentElement,
        },
      }}
      {...textFieldProps}
      size={textFieldProps.size ?? 'small'}
      value={valueToRender}
      error={(displayValueFailsToParse || failedValidation || error) && disabled !== true}
      helperText={(() => {
        if (disabled !== true) {
          if (failedValidation) return failedValidation;
          if (displayValueFailsToParse) return 'invalid value';
        }

        return helperText;
      })()}
      onFocus={(event) => {
        if (textFieldProps.onFocus) textFieldProps.onFocus(event);
        handleFocus(event);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();

          if (blurOnEnter !== false) {
            isBlurringDueToEnterKey.current = true;
            event.target.blur();
            isBlurringDueToEnterKey.current = false;
          }

          handleEventAffectingValue(event, 'enter');
        } else {
          if (textFieldProps.onKeyDown) textFieldProps.onKeyDown(event);
        }
      }}
      onChange={(event) => {
        if (textFieldProps.onChange) textFieldProps.onChange(event);
        handleEventAffectingValue(event, 'change');
      }}
      onBlur={(event) => {
        if (textFieldProps.onBlur) textFieldProps.onBlur(event);

        // Don't submit if this blur was caused by the `Enter` hotkey; it will trigger its own submission which takes priority
        if (!isBlurringDueToEnterKey.current) handleEventAffectingValue(event, 'blur');
      }}
    />
  );
}
